import React from 'react'
import styled from 'styled-components'
import { parse } from 'query-string'
import reactStringReplace from 'react-string-replace'

import { Container, ContentFrame } from '../../components/content/Content'
import Icon from '../../components/icon/Icon'
import { mediaQueries, palette } from '../../styles/variables'
import { LinkText, Text } from '../../components/topography'
import { translate } from '../../utils/translation'
import { OutlineButton } from '../../components/buttons/Buttons'
import whiteScreenAlert from '../../styles/assets/white_screen_alert.svg'
import reloadIcon from '../../styles/assets/reload.svg'
import {
  kissmetricsRecordingEvent,
  KISSMETRICS_TRACK_EVENTS,
} from '../../utils/kissmetrics'

const Page = styled.div((props) => ({
  flex: 1,
  width: props.collapse ? 'calc(100% - 84px)' : 'calc(100% - 220px)',
  overflow: 'auto',
  [mediaQueries.tablet]: {
    width: 'calc(100% - 84px)',
  },
  [mediaQueries.mobile]: {
    width: '100%',
  },
}))

const ErrorBoundaryContainer = styled(Container)(() => ({
  alignItems: 'center',
  justifyContent: 'center',
  height: '80%',
  [mediaQueries.mobile]: {
    height: 'fit-content',
    minHeight: '100%',
  },
}))

const ErrorBoundaryWrapper = styled.div(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '500px',
  [mediaQueries.mobile]: {
    maxWidth: '100%',
  },
}))

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError() {
    // Update state so the next render will show the fallback UI.
    return { hasError: true }
  }

  componentDidUpdate(prevProps) {
    const { location } = this.props
    if (prevProps.location.pathname !== location.pathname) {
      this.setState({ hasError: false })
    }
  }

  componentDidCatch(error, errorInfo) {
    const { location } = this.props
    console.error('Error Boundary', error, errorInfo)
    let path = location.pathname.split('/').slice(2, 4).join('/')
    const parsed = parse(location.search)
    if (parsed.view) {
      path = `${path}/${parsed.view}`
    }

    kissmetricsRecordingEvent(KISSMETRICS_TRACK_EVENTS.wsod, {
      report: path,
      error_message: error.message,
      error_stack: error.stack,
      error_info: errorInfo.componentStack,
    })
  }

  refreshPage = () => {
    window.location.reload()
  }

  render() {
    const { hasError } = this.state
    const { children } = this.props
    if (hasError) {
      return (
        <Page>
          <ContentFrame>
            <ErrorBoundaryContainer>
              <ErrorBoundaryWrapper>
                <Icon src={whiteScreenAlert} />
                <Text
                  fontSize="16px"
                  lineHeight="28px"
                  margin="16px 0 0 0"
                  textAlign="center"
                  dangerouslySetInnerHTML={{
                    __html: translate('general_error_page_message'),
                  }}
                  boldColor={palette.darkGray}
                />
                <Text lineHeight="24px" margin="16px 0 0 0" textAlign="center">
                  {reactStringReplace(
                    translate('if_issue_persists', translate('support_email')),
                    translate('support_email'),
                    (match, i) => {
                      return (
                        <LinkText
                          target="_blank"
                          key={`privacy-${i}`}
                          href="mailto:support@ubersuggest.com"
                        >
                          {match}
                        </LinkText>
                      )
                    },
                  )}
                </Text>
                <OutlineButton
                  static
                  style={{
                    width: 'fit-content',
                    height: '40px',
                    flex: 'inherit',
                    fontSize: '14px',
                    letterSpacing: '0.08em',
                    padding: '14px 56px',
                    marginTop: '24px',
                  }}
                  onClick={this.refreshPage}
                >
                  <Icon src={reloadIcon} margin="0 8px 0 0" />
                  {translate('refresh_button')}
                </OutlineButton>
              </ErrorBoundaryWrapper>
            </ErrorBoundaryContainer>
          </ContentFrame>
        </Page>
      )
    }

    return children
  }
}

export default ErrorBoundary
